// Geometry for the replay rail, kept out of the component.
//
// This project tests pure functions and keeps JSX thin, and there is no
// DOM test environment. So the parts of the rail that can be wrong — bar
// heights, which hours are day starts, where a pointer lands — live here
// where they can be asserted, and ReplayRail.tsx only draws them.

import type { HourBucket } from './replay';

/** SVG user units for the rail's height. The viewBox is stretched to fit,
 *  so this is arbitrary; what matters is that the origin is the BOTTOM. */
export const RAIL_VIEW_H = 100;

/** The shortest a bar may draw and still be a mark rather than nothing.
 *  An hour holding one event against a peak of a hundred would otherwise
 *  round to an invisible line. */
const MIN_BAR_FRACTION = 0.06;

/** The three kinds the rail tracks, in the order they stack. */
export const RAIL_SERIES = ['moves', 'comments', 'prs'] as const;
export type RailSeriesName = (typeof RAIL_SERIES)[number];

/** The colours, which are the SAME as the indicators on the board. The same
 *  kind of thing should look the same wherever it appears — a magenta bar
 *  on the rail and a magenta diamond over a piece are the same event. */
export const RAIL_COLOURS: Record<RailSeriesName, string> = {
  moves: '#fbbf24',
  comments: '#38bdf8',
  prs: '#e879f9',
};

export const RAIL_LABELS: Record<RailSeriesName, string> = {
  moves: 'moves',
  comments: 'comments',
  prs: 'PRs',
};

/**
 * The backwards-move ticks, as a path.
 *
 * Drawn ABOVE the bars rather than stacked into them, because direction is
 * not volume: a regression is a subset of the moves already in the bar, so
 * stacking it would count it twice. They are deliberately tall and thin —
 * they are rare, and the whole point is that they are the thing you notice.
 */
export function backwardsTicks(buckets: HourBucket[]): string {
  let d = '';
  for (let i = 0; i < buckets.length; i += 1) {
    if (buckets[i]!.backwards <= 0) continue;
    // A hairline from the top of the viewBox downward, so it reads as a
    // marker hung over the hour rather than as another band in the stack.
    d += `M${i} 0 L${i} ${RAIL_VIEW_H * 0.34} `;
  }
  return d;
}

export interface RailPaths {
  /** One filled path per kind, STACKED: each begins where the one below it
   *  ends, so the bar's height is the hour's total and its bands are the
   *  composition. */
  series: Array<{ name: RailSeriesName; d: string }>;
  /** The busiest hour's total, for the header readout. */
  peak: number;
}

/**
 * STACKED bars, one filled path per kind.
 *
 * Was two overlapping line series — every event in amber with the comment
 * subset drawn over it — which worked for two and does not for three:
 * three sets of overlapping lines is a braid, not a chart. Stacking says
 * the same thing and one more: the height is the hour's volume AND the
 * bands are what the hour was MADE of. A tall bar that is all comments is
 * a different day from a tall bar that is all merges.
 *
 * Heights are normalised against the busiest hour, with a floor so an hour
 * holding a single event is still a mark rather than nothing.
 *
 * An hour with nothing in it contributes no subpath at all: a zero-height
 * rectangle still costs a path segment, and there are 480 hours in a
 * window.
 */
export function railPaths(buckets: HourBucket[]): RailPaths {
  const empty = RAIL_SERIES.map((name) => ({ name, d: '' }));
  if (buckets.length === 0) return { series: empty, peak: 0 };

  let peak = 0;
  for (const b of buckets) if (b.count > peak) peak = b.count;

  const frac = (v: number) => (v <= 0 ? 0 : Math.max(MIN_BAR_FRACTION, v / (peak || 1)));

  const out: Record<RailSeriesName, string> = { moves: '', comments: '', prs: '' };

  for (let i = 0; i < buckets.length; i += 1) {
    const b = buckets[i]!;
    // Stacked from the baseline upward, in RAIL_SERIES order.
    let base = RAIL_VIEW_H;
    for (const name of RAIL_SERIES) {
      const v = b[name];
      if (v <= 0) continue;
      const h = frac(v) * RAIL_VIEW_H;
      const top = base - h;
      // A rectangle as a filled subpath: across the top, down the far side,
      // back along the bottom, closed.
      out[name] += `M${i} ${top} L${i + 1} ${top} L${i + 1} ${base} L${i} ${base} Z `;
      base = top;
    }
  }

  return { series: RAIL_SERIES.map((name) => ({ name, d: out[name] })), peak };
}

/**
 * Indices of the hours that begin a LOCAL day.
 *
 * Local, because the rail's ticks have to land on the operator's midnight.
 * The window's start is arbitrary, so "every 24th bucket" would draw ticks
 * at whatever o'clock the window happened to open.
 *
 * Capped at `max`, so a long window cannot fill the rail with more labels
 * than can be read.
 */
export function localDayTicks(buckets: HourBucket[], max = 40): number[] {
  const out: number[] = [];
  for (let i = 0; i < buckets.length; i += 1) {
    if (new Date(buckets[i]!.startMs).getHours() === 0) {
      out.push(i);
      if (out.length >= max) break;
    }
  }
  return out;
}

/** Where an instant sits along the rail, 0 to 1. Clamped, so a playhead
 *  outside the window pins to an end instead of drawing off-canvas. */
export function fractionAt(ms: number, fromMs: number, toMs: number): number {
  const span = toMs - fromMs;
  if (span <= 0) return 0;
  const f = (ms - fromMs) / span;
  return f < 0 ? 0 : f > 1 ? 1 : f;
}

/** The inverse: where a pointer at `fraction` along the rail lands in
 *  time. This is what makes the rail draggable. */
export function instantAtFraction(fraction: number, fromMs: number, toMs: number): number {
  const clamped = fraction < 0 ? 0 : fraction > 1 ? 1 : fraction;
  return fromMs + clamped * (toMs - fromMs);
}

/** Convert a pointer position to a fraction of the rail's width. Widths of
 *  zero (a hidden or not-yet-laid-out element) yield 0 rather than NaN,
 *  which would otherwise put the playhead at an unreadable date. */
export function pointerFraction(clientX: number, left: number, width: number): number {
  if (width <= 0) return 0;
  return pointerFractionClamped((clientX - left) / width);
}

function pointerFractionClamped(f: number): number {
  return f < 0 ? 0 : f > 1 ? 1 : f;
}
